import React from "react";
import { ArticleProductCard } from "@/components/articles/ArticleProductCard";
import { ArticleProductThumbnail } from "@/components/articles/ArticleProductThumbnail";
import { ArticleToolCard } from "@/components/articles/ArticleToolCard";
import type { ArticleProduct } from "@/domains/articles/ArticleProduct";
import { resolveArticleTool } from "@/domains/articles/ArticleTool";
import { MarkdownRenderer, type MarkdownStyles } from "@/lib/MarkdownRenderer";

const ARTICLE_MARKDOWN_STYLES: MarkdownStyles = {
  h1: "mt-8 mb-4 text-2xl font-bold text-primary-800 scroll-mt-28",
  h2: "heading-wrap mt-12 mb-5 pb-2 text-xl md:text-2xl font-bold text-primary-800 border-b-2 border-primary-100 scroll-mt-28 first:mt-0",
  h3: "heading-wrap mt-8 mb-3 pl-3 text-lg font-bold text-primary-700 border-l-4 border-primary-300 scroll-mt-28",
  p: "mb-5 text-base leading-8 text-text",
  ul: "mb-6 pl-6 list-disc marker:text-primary-400 space-y-2",
  li: "text-base leading-7 text-text",
  blockquote:
    "my-6 rounded-r-xl border-l-4 border-secondary-300 bg-secondary-50 px-5 py-4 text-text",
  blockquoteParagraph: "leading-7",
  a: "text-primary-600 underline decoration-primary-300 underline-offset-2 hover:text-primary-800",
  strong: "font-bold text-primary-800",
  sup: "ml-0.5 text-[0.7em] align-super",
  supLink: "font-medium text-primary-500 no-underline hover:text-primary-700",
  tableWrapper: "my-6 overflow-x-auto rounded-xl border border-primary-100",
  table: "w-full text-sm text-text",
  th: "border-b border-primary-100 bg-primary-50 px-4 py-3 font-bold text-primary-800 whitespace-nowrap",
  tbody: "divide-y divide-primary-100 bg-white",
  td: "px-4 py-3 align-top leading-6",
  details:
    "my-6 rounded-xl border border-primary-100 bg-primary-50/40 px-4 py-3",
  summary:
    "cursor-pointer select-none text-sm font-bold text-primary-700 marker:text-primary-400",
  detailsBody: "mt-3 text-sm [&_p]:mb-3 [&_p]:text-sm [&_p]:leading-7",
  message:
    "my-6 flex gap-3 rounded-xl border border-primary-200 bg-primary-50 px-4 py-4",
  messageAlert:
    "my-6 flex gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-4",
  messageIcon:
    "mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-400 text-xs font-bold text-white",
  messageAlertIcon:
    "mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500 text-xs font-bold text-white",
  messageBody: "min-w-0 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_p]:leading-7",
};

export const ArticleBody: React.FC<{
  body: string;
  products: readonly ArticleProduct[];
}> = ({ body, products }) => {
  const productsById = new Map(
    products.map((product) => [product.id, product]),
  );
  const resolveProduct = (id: string | undefined): ArticleProduct => {
    const product = id === undefined ? undefined : productsById.get(id);
    if (product === undefined) {
      throw new Error(
        `記事本文の商品ディレクティブ(id="${String(id)}")に対応する商品が解決されていません`,
      );
    }
    return product;
  };

  return (
    <article>
      <MarkdownRenderer
        content={body}
        styles={ARTICLE_MARKDOWN_STYLES}
        directives={{
          product: (attributes, key) => (
            <ArticleProductCard
              key={key}
              product={resolveProduct(attributes.id)}
            />
          ),
          tool: (attributes, key) => (
            <ArticleToolCard key={key} tool={resolveArticleTool(attributes)} />
          ),
        }}
        textDirectives={{
          product: (attributes, key) => (
            <ArticleProductThumbnail
              key={key}
              product={resolveProduct(attributes.id)}
            />
          ),
        }}
      />
    </article>
  );
};
